import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  FileText, Download, Inbox, Share2, ShieldAlert, Bell, FileUp, 
  LogOut, LayoutDashboard, Settings, User, Shield, MessageCircle, Menu, X, ShieldCheck, Trash2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function DashboardLayout({ children, user, onLogout, stats = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    {
      label: 'Overview',
      icon: LayoutDashboard,
      path: '/dashboard',
      exact: true
    },
    {
      label: 'My Documents',
      icon: FileText,
      path: '/dashboard/documents',
      count: stats.documents
    },
    {
      label: 'Upload Document',
      icon: FileUp,
      path: '/dashboard/upload'
    },
    {
      label: 'Deleted Documents',
      icon: Trash2,
      path: '/dashboard/deleted-documents'
    },
    {
      label: 'Fetch from APIs',
      icon: Download,
      path: '/dashboard/fetch-docs'
    },
    {
      label: 'Access Requests',
      icon: Inbox,
      path: '/dashboard/requests',
      count: stats.requests,
      alert: stats.requests > 0
    },
    {
      label: 'Active Shares',
      icon: Share2,
      path: '/dashboard/shares',
      count: stats.shares
    },
    {
      label: 'Messages',
      icon: MessageCircle,
      path: '/dashboard/messages'
    },
    {
      label: 'Evidence Vault',
      icon: ShieldAlert,
      path: '/dashboard/evidence',
      count: stats.evidence
    },
    {
      label: 'Alerts',
      icon: Bell,
      path: '/dashboard/alerts',
      count: stats.alerts,
      alert: stats.alerts > 0
    },
    {
      label: 'Verify Watermark',
      icon: ShieldCheck,
      path: '/dashboard/verify-watermark'
    }
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const SidebarContent = () => (
    <>
      {/* Logo & Brand */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" 
               style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">UDT System</h1>
            <p className="text-xs text-gray-500">Secure Document Transfer</p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || 'user@example.com'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-2 sm:px-3 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`
                  w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-150
                  ${active 
                    ? 'bg-blue-50 text-blue-700 shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-50'}
                `}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </div>
                {(item.count > 0 || item.alert) && (
                  <Badge 
                    variant={item.alert ? "destructive" : "secondary"}
                    className="h-5 px-2 text-xs"
                  >
                    {item.count}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Logout */}
      <div className="p-3 sm:p-4 border-t border-gray-200">
        <Button
          variant="outline"
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          onClick={() => {
            if (onLogout) onLogout();
            else {
              localStorage.clear();
              navigate('/login');
            }
          }}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-gray-200 flex-col">
        <SidebarContent />
      </aside>

      {/* Sidebar - Mobile */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-300 ease-in-out lg:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-600"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            <h1 className="text-lg font-bold text-gray-900">UDT System</h1>
          </div>
          <div className="w-8" /> {/* Spacer for centering */}
        </header>

        {/* Scrollable Content Area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="min-h-full w-full" style={{ background: 'linear-gradient(135deg, #E8F2FF 0%, #F5F9FF 100%)' }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
